import { OrderDetails } from '../api/fetchAllHEOrder';
import { useRef, useState } from 'react';
import cancelOrderOrBooking from '../api/cancelOrderOrBooking';
import { Modal, notification, Radio, Space } from 'antd';
import { captureError } from './captureError';
import { track } from '../tracking';
import { pharmaCancellationReasons } from '../components/prescriptionPad/rightPanel/finishScreen/OrderMedicineUtils';
import { labCancellationReasons } from '../components/prescriptionPad/rightPanel/finishScreen/BookLabTestsUtils';
import { ACTIVE_TAB, ActiveTab } from './HEDashboard';
import { getMySingleDoctorId } from '../store/idbHandler';

const CancelOrderOrBooking: React.FC<{
    activeTab: ActiveTab;
    orderDetails: OrderDetails;
    onClose: () => void;
    onRefresh?: () => void;
}> = ({ activeTab, orderDetails, onClose, onRefresh }) => {
    const otherReasons = new Set(['others', 'others_remarks']);
    const isForMeds = activeTab === ACTIVE_TAB.MEDS;
    const cancellationReasons = isForMeds ? pharmaCancellationReasons : labCancellationReasons;
    const [cancelReason, setCancelReason] = useState('');
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const [isLoading, setIsLoading] = useState(false);
    const { order_id, id } = orderDetails;
    const docId = getMySingleDoctorId() || '';

    const onConfirmClick = async (): Promise<void> => {
        const otherReason = inputRef.current?.value.trim();
        if (!cancelReason) {
            notification.error({
                message: 'Please select reason',
                duration: 5,
            });
            return;
        }
        if (otherReasons.has(cancelReason) && !otherReason) {
            notification.error({
                message: 'Please add reason',
                duration: 5,
            });
            return;
        }
        setIsLoading(true);
        try {
            const remark = otherReason || cancellationReasons[cancelReason];
            const { success } = await cancelOrderOrBooking(
                docId,
                activeTab,
                order_id || id,
                cancelReason,
                remark,
            );
            if (success) {
                onRefresh && onRefresh();
                notification.success({
                    message: `${isForMeds ? 'Order' : 'Booking'} cancelled successfully!`,
                    duration: 5,
                });
                track('CANCEL_ORDER_OR_BOOKING_SUCCESS', {
                    activeTab,
                    order_id: order_id || id,
                    cancelReason,
                    otherReason,
                });
            } else {
                notification.error({
                    message: `Error while cancelling ${isForMeds ? 'order' : 'booking'}!`,
                    duration: 5,
                });
            }
        } catch (error) {
            captureError(error as Error);
            track('ERROR', {
                on: 'cancelOrderOrBooking',
                error: (error as Error).message,
            });
            notification.error({
                message: `Error while cancelling ${isForMeds ? 'order' : 'booking'}! [${
                    (error as Error).message
                }]`,
                duration: 5,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const disabled = isLoading || !cancelReason?.trim();

    return (
        <Modal
            onCancel={onClose}
            width={380}
            closable={false}
            visible={true}
            destroyOnClose
            footer={null}
            bodyStyle={{
                padding: '0px',
                borderRadius: '8px !important',
                maxHeight: '380px',
                display: 'flex',
                flexDirection: 'column',
            }}
            className="overflow-hidden rounded-8"
            style={{ maxHeight: '380px' }}
        >
            <div className="h-full flex flex-col bg-white rounded-4 overflow-hidden">
                <div className="p-16 flex items-center justify-between">
                    <p className="font-700 text-16">Do you want to cancel order?</p>
                    <button disabled={isLoading} onClick={onClose} className="disabled:opacity-70">
                        <svg
                            className="w-14 h-14 fill-text-secondary"
                            viewBox="0 0 12 12"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M10.75 9.71875C11.0312 10.0312 11.0312 10.5 10.75 10.7812C10.4375 11.0938 9.96875 11.0938 9.6875 10.7812L6 7.0625L2.28125 10.7812C1.96875 11.0938 1.5 11.0938 1.21875 10.7812C0.90625 10.5 0.90625 10.0312 1.21875 9.71875L4.9375 6L1.21875 2.28125C0.90625 1.96875 0.90625 1.5 1.21875 1.21875C1.5 0.90625 1.96875 0.90625 2.25 1.21875L6 4.96875L9.71875 1.25C10 0.9375 10.4688 0.9375 10.75 1.25C11.0625 1.53125 11.0625 2 10.75 2.3125L7.03125 6L10.75 9.71875Z" />
                        </svg>
                    </button>
                </div>

                <div className="flex-1 overflow-scroll p-16 border-t border-b border-neutral-7 space-y-6 text-darwin-neutral-900">
                    <p className="text-13" style={{ color: '#616A8F' }}>
                        Select the reason
                    </p>

                    <Radio.Group
                        onChange={(e) => setCancelReason(e.target.value)}
                        value={cancelReason}
                    >
                        <Space direction="vertical">
                            {Object.entries(cancellationReasons).map((i, index) => (
                                <Radio
                                    value={i[0]}
                                    key={index}
                                    className={`${i[0] === cancelReason ? 'font-700' : ''}`}
                                >
                                    {i[1]}
                                </Radio>
                            ))}
                        </Space>
                    </Radio.Group>
                    {otherReasons.has(cancelReason) && (
                        <textarea
                            className="w-320 h-72 mt-12 resize-none border border-neutral-7 rounded-8 p-12"
                            ref={inputRef}
                            placeholder="Please add your reason here..."
                        ></textarea>
                    )}
                </div>

                <div className="p-16 flex items-center justify-between">
                    <button
                        className="font-700 text-darwin-primary-500 disabled:opacity-70"
                        onClick={onClose}
                        disabled={disabled}
                    >
                        Cancel
                    </button>

                    <button
                        className="flex items-center justify-center space-x-10 px-12 py-8 text-white bg-darwin-primary-500 rounded-3 font-700 disabled:opacity-70"
                        onClick={onConfirmClick}
                        disabled={disabled}
                    >
                        {isLoading ? (
                            <svg
                                className="w-16 h-16 fill-white animate-spin"
                                role="img"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                            >
                                <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                            </svg>
                        ) : (
                            <svg
                                className="w-16 h-16 fill-white"
                                viewBox="0 0 18 14"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M9 0C9.4375 0 9.84375 0.25 10.0625 0.625L16.8125 12.125C17.0312 12.5312 17.0312 13 16.8125 13.375C16.5938 13.7812 16.1875 14 15.75 14H2.25C1.78125 14 1.375 13.7812 1.15625 13.375C0.9375 13 0.9375 12.5312 1.15625 12.125L7.90625 0.625C8.125 0.25 8.53125 0 9 0ZM9 4C8.5625 4 8.25 4.34375 8.25 4.75V8.25C8.25 8.6875 8.5625 9 9 9C9.40625 9 9.75 8.6875 9.75 8.25V4.75C9.75 4.34375 9.40625 4 9 4ZM10 11C10 10.4688 9.53125 10 9 10C8.4375 10 8 10.4688 8 11C8 11.5625 8.4375 12 9 12C9.53125 12 10 11.5625 10 11Z" />
                            </svg>
                        )}
                        <p>Confirm</p>
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default CancelOrderOrBooking;
